import React from "react";
import PropTypes from "prop-types";
import { formatMoney, formatMoneyCompact } from "../formatters";
import ThresholdBar from "../../budget-enforcement/components/ThresholdBar";
import { authorizationOutlook, riskFocus, formatPeriodName } from "../utils/budgetOutlook";
import { Panel, PanelSection } from "./Panel";

// The risk column answers three questions, in the order a budget owner asks them:
//
//   1. Are we within the authorization?   -> Outlook          (clear / projected breach)
//   2. Where is the greatest risk?        -> Exposure         (largest overage / closest to cap)
//   3. Can I trust that projection?       -> Forecast confidence
//
// Both of the first two are ONE component with two states rather than two sections. A
// section permanently headed "Earliest breach" gives breach the visual priority even when
// there is no breach, and one permanently headed "Worst variance" has to invent a worst
// period even when every period is comfortably inside its amount. Scope overlaps get no
// section at all unless one exists: "No overlaps detected" is not decision-relevant.
//
// This is SUPPORTING context, not a second workspace. Each block used to carry its own
// border, its own icon, its own uppercase label and two sizes of text, so four of them
// competed with the authorization beside them. They are now sections of one panel: no
// icons, title-case headings, and values at body size — the €2,000 in the centre column is
// the page's dominant figure and nothing here should rival it.

const SkeletonBody = () => <div className="mt-2 h-8 bg-surface-2 rounded-ctrl animate-pulse" />;

const capitalize = (value) => (value ? value.charAt(0).toUpperCase() + value.slice(1) : "");

// Amounts on `risk` are plain currency units (e.g. euros), not cents — the money
// formatters take cents.
const money = (value, currency) => formatMoney(Math.round((value || 0) * 100), currency);
const compact = (value, currency) => formatMoneyCompact(Math.round((value || 0) * 100), currency);

// The value a section exists to state. Weight and colour carry the emphasis; size does not,
// because size is what would make this column compete with the workspace.
const Value = ({ children, tone = "neutral" }) => (
  <p
    className={`text-sm font-semibold tabular-nums leading-tight ${
      tone === "danger" ? "text-danger" : tone === "warning" ? "text-warning" : "text-text"
    }`}
  >
    {children}
  </p>
);

// ── 1. Outlook ──────────────────────────────────────────────────────────────────────
// Same section, two states. Clear says so plainly and quantifies the room left; a
// projected breach names the period, the money and the decision it forces.
const OutlookSection = ({ risk, riskLoading, currency }) => {
  const outlook = authorizationOutlook(risk);
  const breached = outlook.state === "breach";

  return (
    <PanelSection title={breached ? "Projected breach" : "Budget outlook"}>
      {riskLoading && !risk ? (
        <SkeletonBody />
      ) : breached ? (
        <>
          <Value tone="warning">{formatPeriodName(outlook.period)}</Value>
          <p className="mt-1 text-xs text-text-2 tabular-nums">
            {compact(outlook.overBy, currency)} projected over the authorized amount
            {outlook.overPct != null ? ` · ${outlook.overPct}%` : ""}
          </p>
          <p className="mt-2 text-xs font-medium text-warning">Owner review required</p>
        </>
      ) : outlook.state === "clear" ? (
        <>
          <Value>No breach projected</Value>
          <p className="mt-1 text-xs text-text-2 tabular-nums">
            {compact(outlook.headroom, currency)} projected headroom over {outlook.periodCount}{" "}
            period{outlook.periodCount === 1 ? "" : "s"}
          </p>
          {outlook.runwayThroughPeriod ? (
            <p className="mt-1 text-xs text-text-3">Through {formatPeriodName(outlook.runwayThroughPeriod)}</p>
          ) : null}
        </>
      ) : (
        <>
          <p className="text-xs text-text-2">Outlook not available yet</p>
          <p className="mt-1 text-xs text-text-3">No projection covers these periods.</p>
        </>
      )}
    </PanelSection>
  );
};

// ── 2. Exposure ─────────────────────────────────────────────────────────────────────
// One horizontal utilization track and nothing else: period, money, how full, headroom.
// No legend, no axis, no interpretation. Ranked by money at risk (see budgetOutlook), so
// a period 63% UNDER its amount can never be presented as the worst thing on the page.
const ExposureSection = ({ risk, riskLoading, currency }) => {
  const focus = riskFocus(risk);
  const over = focus?.kind === "over";

  return (
    <PanelSection title={over ? "Highest projected variance" : "Closest to cap"}>
      {riskLoading && !risk ? (
        <SkeletonBody />
      ) : !focus ? (
        <>
          <p className="text-xs text-text-2">No projection yet</p>
          <p className="mt-1 text-xs text-text-3">Period exposure appears once a forecast covers these periods.</p>
        </>
      ) : (
        <>
          {over ? (
            <>
              <Value tone="danger">+{compact(focus.delta, currency)}</Value>
              <p className="mt-0.5 text-xs text-text-2">
                over budget · {formatPeriodName(focus.period)}
              </p>
            </>
          ) : (
            <>
              <Value>{formatPeriodName(focus.period)}</Value>
              <p className="mt-0.5 text-xs text-text-2 tabular-nums">
                {compact(focus.projected, currency)} of {compact(focus.authorized, currency)}
              </p>
            </>
          )}

          {/* No cap marker while the projection is inside the authorization: the end of the
              track already is 100%, so the tick only repeats it. Once the bar rescales past
              100% to keep the overage visible, the tick genuinely marks where the
              authorization sits inside a longer track, and earns its place. */}
          <div className="mt-3">
            <ThresholdBar
              nowPct={focus.usedPct}
              marks={over ? { hard: 100 } : undefined}
              tone={over ? "danger" : "neutral"}
              height={8}
            />
          </div>

          <div className="mt-2 flex items-baseline justify-between gap-2 text-xs tabular-nums">
            <span className="font-medium text-text">{focus.usedPct}%</span>
            <span className="text-text-3">
              {over
                ? `${money(focus.authorized, currency)} authorized · ${money(focus.projected, currency)} projected`
                : `${compact(focus.headroom, currency)} headroom`}
            </span>
          </div>

          {over && focus.overCount > 1 ? (
            <p className="mt-2 text-xs text-text-3">
              {focus.overCount} periods projected over their amount.
            </p>
          ) : null}
        </>
      )}
    </PanelSection>
  );
};

// ── 3. Scope overlap (exception only) ───────────────────────────────────────────────
const OverlapSection = ({ risk }) => {
  const overlaps = risk?.overlaps || [];
  if (overlaps.length === 0) return null;
  return (
    <PanelSection title="Scope overlap">
      <p className="text-xs text-text-2 mb-2">
        Spend in this scope is also authorized by {overlaps.length === 1 ? "another budget" : `${overlaps.length} other budgets`}.
      </p>
      <ul className="flex flex-col gap-1">
        {overlaps.map((overlap) => (
          <li key={overlap.budgetId} className="text-xs">
            <span className="font-medium text-text">{overlap.name}</span>{" "}
            <span className="text-text-3">{overlap.sharedLabel}</span>
          </li>
        ))}
      </ul>
    </PanelSection>
  );
};

// ── 4. Forecast confidence ──────────────────────────────────────────────────────────
const ConfidenceSection = ({ risk, riskLoading }) => (
  <PanelSection title="Forecast confidence">
    {riskLoading && !risk ? (
      <SkeletonBody />
    ) : !risk?.confidence ? (
      <p className="text-xs text-text-2">Not yet available</p>
    ) : (
      <>
        <Value>{capitalize(risk.confidence.level)}</Value>
        <p className="mt-1 text-xs text-text-3">{risk.confidence.basis}</p>
      </>
    )}
  </PanelSection>
);

const BudgetRiskPanel = ({ hasPeriods, risk = null, riskLoading = false, currency = "EUR" }) => {
  if (!hasPeriods) {
    // Definition mode — preview placeholders
    return (
      <Panel className="min-w-0">
        <PanelSection title="Affected spend">
          <p className="text-xs text-text-2">Preview data will appear here once connected</p>
          <div className="mt-2 h-8 bg-surface-2 rounded-ctrl animate-pulse" />
        </PanelSection>
        <PanelSection title="Forecast preview">
          <p className="text-xs text-text-2">Preview data will appear here once connected</p>
          <div className="mt-2 h-8 bg-surface-2 rounded-ctrl animate-pulse" />
        </PanelSection>
        <PanelSection title="Overlaps">
          <p className="text-xs text-text-2">Preview data will appear here once connected</p>
        </PanelSection>
      </Panel>
    );
  }

  return (
    <Panel className="min-w-0">
      <OutlookSection risk={risk} riskLoading={riskLoading} currency={currency} />
      <ExposureSection risk={risk} riskLoading={riskLoading} currency={currency} />
      <OverlapSection risk={risk} />
      <ConfidenceSection risk={risk} riskLoading={riskLoading} />
    </Panel>
  );
};

Value.propTypes = {
  children: PropTypes.node,
  tone: PropTypes.oneOf(["neutral", "warning", "danger"]),
};

const sectionPropTypes = {
  risk: PropTypes.object,
  riskLoading: PropTypes.bool,
  currency: PropTypes.string,
};

OutlookSection.propTypes = sectionPropTypes;
ExposureSection.propTypes = sectionPropTypes;
OverlapSection.propTypes = sectionPropTypes;
ConfidenceSection.propTypes = sectionPropTypes;

BudgetRiskPanel.propTypes = {
  hasPeriods: PropTypes.bool.isRequired,
  risk: PropTypes.object,
  riskLoading: PropTypes.bool,
  currency: PropTypes.string,
};

export default BudgetRiskPanel;
